// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList from "@insite/content-library/Components/CardList";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ContentPageModel } from "@mcmc/App/Services/ContentPageSearchService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import {
    getContentPageDataview,
    getSearchType,
    isContentSearchMode,
    SearchTypes,
} from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import { ProductListPageContext } from "@mcmc/Overrides/Pages/ProductListPage";
import { productListStyles } from "@mcmc/Overrides/Widgets/ProductList/ListCommon.styles";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import Notify from "@mcmc/XDL/Components/Notify";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => ({
    contentPageDataView: getContentPageDataview(state),
    isContentSearch: isContentSearchMode(state),
    searchType: getSearchType(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ContentPageCardStyles {
    pageWrapper?: InjectableCss;
    resourceWrapper?: InjectableCss;
    resourceInfo?: InjectableCss;
    tagsWrapper?: InjectableCss;
    tagLabel?: TypographyPresentationProps;
    tagValue?: TypographyPresentationProps;
    publishedOn?: TypographyPresentationProps;
}

const contentPageCardStyles: ContentPageCardStyles = {
    pageWrapper: {
        css: css`
            width: 100%;
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 30px;
            margin-bottom: 30px;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    resourceWrapper: {
        css: css`
            padding: 0;
            width: 100%;
            margin-bottom: 30px;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    resourceInfo: {
        css: css`
            padding: 30px 35px;
            border-bottom: 1px solid ${getColor("common.border")};
            &:last-child {
                border: none;
            }
        `,
    },
    tagsWrapper: {
        css: css`
            padding: 10px 25px;
        `,
    },
    publishedOn: {
        variant: "p",
        weight: "bold",
        css: css`
            margin-bottom: 10px;
        `,
    },
    tagLabel: {
        size: 12,
        lineHeight: "14px",
        transform: "uppercase",
        css: css`
            padding: 5px 10px;
            display: inline-block;
        `,
    },
    tagValue: {
        css: css`
            color: ${getColor("primary")};
            &:after {
                content: " | ";
                color: ${getColor("secondary")};
            }
            &:last-child:after {
                content: "";
            }
        `,
    },
};

const styles = productListStyles;

const ContentPageList: FC<Props> = ({ id, contentPageDataView, isContentSearch, searchType }) => {
    if (!isContentSearch || !contentPageDataView) {
        return null;
    }

    if (contentPageDataView?.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="contentPagesListSpinner" />
            </StyledWrapper>
        );
    }
    if (!contentPageDataView?.value) {
        return null;
    }

    if (contentPageDataView.value?.contents.length === 0) {
        return (
            <StyledWrapper {...styles.notFoundWrapper}>
                <Notify
                    messageType="danger"
                    hideCloseButton={true}
                    body={
                        searchType === SearchTypes.PAGES
                            ? siteMessage("McMc_Search_NoPagesFound")
                            : siteMessage("McMc_Search_NoResourcesFound")
                    }
                />
            </StyledWrapper>
        );
    }

    const pageList = contentPageDataView?.value.contents.map(page => {
        if (page.pageType.toLowerCase() === SearchTypes.PAGES) {
            return <PageCard key={`${page.title}_${page.url}`} page={page} />;
        }
        if (page.pageType.toLowerCase() === SearchTypes.RESOURCES) {
            return <ResourceCard key={`${page.title}_${page.url}`} page={page} />;
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <CardList extendedStyles={styles.cardList} data-test-selector="contentPageListCardList">
                {pageList}
            </CardList>
        </StyledWrapper>
    );
};

const ResourceCard = ({ page }: { page: ContentPageModel }) => {
    return (
        <StyledBox {...contentPageCardStyles.resourceWrapper}>
            <StyledWrapper {...contentPageCardStyles.resourceInfo}>
                <Link href={page.url} {...styles.cardTitleLink}>
                    {page.title}
                </Link>
                <Typography {...contentPageCardStyles.publishedOn}>{page.publishedOn}</Typography>
                <MultilineEllipsis content={page.pageSummary} maxLines={2} forwardAs="p" lineHeight="16px" />
            </StyledWrapper>
            <StyledWrapper {...contentPageCardStyles.tagsWrapper}>
                {page.articleType && renderTags("type", [page.articleType])}
                {renderTags("tags", page.tags)}
            </StyledWrapper>
        </StyledBox>
    );
};

const renderTags = (label: string, tags: string[]) => {
    if (tags.length === 0) {
        return;
    }
    return (
        <Typography {...contentPageCardStyles.tagLabel}>
            {translate(label)}:&nbsp;
            {tags.map(tag => (
                <Typography key={tag} {...contentPageCardStyles.tagValue}>
                    {tag}
                </Typography>
            ))}
        </Typography>
    );
};

const PageCard = ({ page }: { page: ContentPageModel }) => {
    return (
        <StyledWrapper {...contentPageCardStyles.pageWrapper}>
            <Link href={page.url} {...styles.cardTitleLink}>
                {page.title}
            </Link>
            <MultilineEllipsis content={page.pageSummary} maxLines={2} forwardAs="p" lineHeight="16px" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ContentPageList),
    definition: {
        group: "Product List",
        displayName: "Content Page List",
        allowedContexts: [ProductListPageContext],
    },
};

export default widgetModule;
